// The install's modules: what the header's module switcher lists, and the
// pages that may open docs' intent pages in an overlay. One install, one
// list, from the deploy's configuration (`VITE_ZEHN_MODULES`, a JSON list of
// `{code, name, url}`), never from a URL or a message.
//
// Pure (no `import.meta.env`), so `npm test` checks it: installModules.test.js.
// modules.js reads the configuration.

const MODULE_CODE = /^zehn-[a-z0-9][a-z0-9-]{0,62}$/;

const text = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * The modules a configuration value names, in its order: entries with a
 * module code, a name and a URL, each code once. Anything else (not JSON, not
 * a list, a malformed entry) is left out, so a typo in the deploy shows fewer
 * modules rather than a broken screen.
 */
export function parseModules(raw) {
    let value = raw;
    if (typeof raw === "string") {
        if (raw.trim() === "") return [];
        try {
            value = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(value)) return [];
    const seen = new Set();
    const modules = [];
    for (const entry of value) {
        if (!entry || typeof entry !== "object") continue;
        const code = text(entry.code);
        const name = text(entry.name);
        const url = text(entry.url);
        if (!MODULE_CODE.test(code) || !name || !url || seen.has(code)) continue;
        seen.add(code);
        modules.push(Object.freeze({ code, name, url }));
    }
    return modules;
}

/**
 * The exact origins of the modules' screens (`https://abat.example.uz`): who
 * may open docs' intent pages. A path-only URL is on `here` (this page's
 * origin); anything that is not http(s) is left out.
 */
export function moduleOrigins(modules, here) {
    const origins = new Set();
    for (const module of Array.isArray(modules) ? modules : []) {
        try {
            const url = new URL(module.url, here);
            if (url.protocol === "https:" || url.protocol === "http:") origins.add(url.origin);
        } catch {
            // not a URL: no origin
        }
    }
    return [...origins];
}

// A client page's origin (VITE_ZEHN_HOSTS): exactly `http(s)://host[:port]`,
// written as the browser writes an origin (lower case, no default port).
const HOST_ORIGIN = /^https?:\/\/[a-z0-9.-]+(?::[0-9]{1,5})?$/;

/**
 * The install's client pages a configuration value names (`VITE_ZEHN_HOSTS`,
 * a JSON list of exact origins such as `"https://portal.example.uz"`): pages
 * of the client's own, its portal, that may frame docs's screens and open
 * its intent pages. Each origin once. An entry that is not exactly an http(s)
 * origin (a path, a query, a wildcard, credentials, not a string) is left out,
 * and so is everything when the value is not a JSON list. A host is never a
 * module: it is not in the switcher and never a companion.
 */
export function parseHosts(raw) {
    let value = raw;
    if (typeof raw === "string") {
        if (raw.trim() === "") return [];
        try {
            value = JSON.parse(raw);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(value)) return [];
    const hosts = new Set();
    for (const entry of value) {
        const origin = text(entry);
        if (!HOST_ORIGIN.test(origin)) continue;
        try {
            if (new URL(origin).origin === origin) hosts.add(origin);
        } catch {
            // not a URL (a port out of range): no host
        }
    }
    return [...hosts];
}

/**
 * Who may open docs's intent pages (connectToHost's allowedHostOrigins): the
 * modules' screens (moduleOrigins), then the install's client pages
 * (parseHosts), each origin once.
 */
export function allowedHostOrigins(modules, hosts, here) {
    return [...new Set([...moduleOrigins(modules, here), ...(Array.isArray(hosts) ? hosts : [])])];
}
